import React, { useRef, useState, useEffect, useId } from "react";
import { Box, TextField, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDarkTheme } from "../../utils/useDarkTheme";
import { getErrorMessageA11yProps, getInputErrorA11yProps } from "../../utils/fieldError.utils";
import { useTranslation } from "../../hooks/useTranslation";

export default function LoginOtpGroupedField({
  value,
  onChange,
  length = 6,
  error,
  helperText,
  primaryColor,
  fontFamily,
  onOtpInputStart,
  onOtpPaste,
  handleOtpKeyPress,
  customDetails,
  autoFocus = false,
  focusKey,
  labelId,
}) {
  const { t } = useTranslation();
  const inputsRef = useRef([]);
  const isDark = useDarkTheme(customDetails);
  const errorId = useId();
  const hasError = Boolean(error);
  const errorMessage =
    typeof helperText === "string"
      ? helperText
      : hasError && helperText
        ? helperText
        : "";
  const [revealedIndex, setRevealedIndex] = useState(null);
  const revealTimerRef = useRef(null);

  const revealAndMask = (idx) => {
    clearTimeout(revealTimerRef.current);
    setRevealedIndex(idx);
    revealTimerRef.current = setTimeout(() => setRevealedIndex(null), 800);
  };

  useEffect(() => () => clearTimeout(revealTimerRef.current), []);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => inputsRef.current[0]?.focus(), 50);
    return () => clearTimeout(timer);
  }, [autoFocus, focusKey, length]);

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/, "");
    if (!val) return;

    const otpArray = value.split("").concat(Array(length).fill("")).slice(0, length);
    const firstEmptyIndex = otpArray.findIndex((digit) => !digit);
    const targetIndex =
      firstEmptyIndex !== -1 && index > firstEmptyIndex
        ? firstEmptyIndex
        : index;

    otpArray[targetIndex] = val[0];
    onChange(otpArray.join(""));
    revealAndMask(targetIndex);

    if (targetIndex < length - 1) {
      inputsRef.current[targetIndex + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    const keysToPrevent = ["PageUp", "PageDown", "ArrowUp", "ArrowDown"];
    if (keysToPrevent.includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "Enter") {
      e.preventDefault();
      e.target.closest("form")?.requestSubmit();
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      const otpArray = value.split("");

      if (otpArray[index]) {
        otpArray[index] = "";
        onChange(otpArray.join(""));
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        otpArray[index - 1] = "";
        onChange(otpArray.join(""));
      }
    }
  };

  const handleFocus = (e) => {
    onOtpInputStart?.("focus");
    e.target.addEventListener(
      "wheel",
      function (evt) {
        evt.preventDefault();
      },
      { passive: false }
    );
  };

  const handlePaste = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (pasted) {
      onChange(pasted);
      const focusIndex = Math.min(pasted.length, length) - 1;
      setTimeout(() => {
        inputsRef.current[focusIndex]?.focus();
      }, 0);
      onOtpPaste?.(pasted);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      <Box
        role="group"
        aria-labelledby={labelId || undefined}
        aria-label={labelId ? undefined : "One-time password"}
        aria-describedby={hasError ? errorId : undefined}
        sx={{ display: "flex", gap: "0.5rem", width: "100%", maxWidth: "340px" }}
      >
        {Array.from({ length }).map((_, idx) => {
          const digitLabel = value[idx]
            ? t("common.labels.otpDigitEntered", { current: idx + 1, total: length })
            : t("common.labels.otpDigit", { current: idx + 1, total: length });

          return (
          <TextField
            key={idx}
            value={value[idx] ? (revealedIndex === idx ? value[idx] : "*") : ""}
            placeholder="—"
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onFocus={handleFocus}
            onPaste={handlePaste}
            autoComplete={idx === 0 ? "one-time-code" : "off"}
            name={idx === 0 ? "otp" : `otp-${idx}`}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              pattern: "[0-9]*",
              onKeyPress: handleOtpKeyPress,
              autoComplete: idx === 0 ? "one-time-code" : "off",
              "aria-label": digitLabel,
              ...getInputErrorA11yProps(hasError, errorId),
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: '52px',
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderRadius: "8px" },
                "& input": {
                  color: isDark ? "#FFFFFF" : "#000000",
                  textAlign: "center",
                  fontSize: "1.5rem",
                },
                "& input::placeholder": {
                  color: isDark ? "#9AA3A8" : "#757575",
                  opacity: 1,
                  textAlign: "center",
                  fontSize: "1rem",
                  lineHeight: "1",
                },
                "&:hover fieldset": { borderColor: primaryColor },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: "2px",
                },
                "&.Mui-error fieldset": { borderColor: "error.main" },
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#FFFFFF" : "#000000",
                WebkitTextFillColor: isDark ? "#FFFFFF" : "#000000",
                caretColor: isDark ? "#FFFFFF" : "#000000",
                padding: { xs: "16.5px 0", md: "12.5px 0" },
              },
              borderRadius: "0px !important",
              fontFamily,
            }}
            error={hasError}
            inputRef={(el) => (inputsRef.current[idx] = el)}
          />
        );
        })}
      </Box>
      {hasError && errorMessage && (
        <Typography
          {...getErrorMessageA11yProps(errorId)}
          color="error"
          fontSize="0.75rem"
          sx={{
            padding: 0,
            mt: "4px",
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.25rem',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: '0.875rem', mt: '0.1rem', flexShrink: 0 }} aria-hidden />
          <span>{errorMessage}</span>
        </Typography>
      )}
    </Box>
  );
}
